/**
 * Whether the inbox chat panel may create a chat session for the current view.
 *
 * This predicate MUST stay in lockstep with the server guard in
 * `applications/api/src/contexts/alby/chat/presentation/chat.resolver.ts`
 * (`userOwnsOrCollaboratesOnConversation`, enforced by both `createChatSession` and
 * `chatSessions`), which admits a conversation the caller's own inbox holds or
 * one whose stitched deal thread they own or actively collaborate on. When the
 * client allowed creation for a conversation the server forbids, the mutation
 * 403s and — before the resolver and the failure latch — stormed a single
 * rejection into an all-day loop (B-1674). Keep both sides admitting the same
 * set.
 *
 * - Insured chat (no conversation): always allowed.
 * - Conversation chat: on the user's own inbox, or on another user's inbox when
 *   the user collaborates on the deal. A deal thread is stitched across
 *   mailboxes, so the copy a broker opens is often not their own; gating on
 *   ownership alone left collaborators locked out of Alby (B-1726).
 */
export const canCreateInboxChatSession = ({
  conversationId,
  isOwnInbox,
  canReplyAsCollaborator,
}: {
  conversationId?: string | null;
  isOwnInbox: boolean;
  canReplyAsCollaborator: boolean;
}): boolean => !conversationId || isOwnInbox || canReplyAsCollaborator;
